import {
    Dialog,
    DialogContent, DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { setPlayerModalUrlParam, usePlayerModalStateValue } from "@/hooks/playerModal";
import { 
    InfoIcon, HistoryIcon, GavelIcon, ShieldCheckIcon, 
    MailIcon, AlertTriangleIcon, ChevronLeftIcon, UserIcon, 
    ClockIcon, CalendarIcon, SendIcon, ShieldAlertIcon,
    Loader2Icon, MessageSquareIcon, CheckCircle2Icon, LogOut
} from "lucide-react";
import { useEffect, useState, useRef, useMemo } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import GenericSpinner from "@/components/GenericSpinner";
import { cn } from "@/lib/utils";
import { useBackendApi } from "@/hooks/fetch";
import { useAdminPerms } from "@/hooks/auth";
import { PlayerModalResp, PlayerModalSuccess } from "@shared/playerApiTypes";
import ModalCentralMessage from "@/components/ModalCentralMessage";
import { txToast } from "@/components/TxToaster";
import BanForm, { BanFormType } from "@/components/BanForm";
import { tsToLocaleDateTimeString, msToDuration } from "@/lib/dateTime";
import DateTimeCorrected from "@/components/DateTimeCorrected";
import PlayerIdsTab from "./PlayerIdsTab";
import PlayerHistoryTab from "./PlayerHistoryTab";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLocation } from "wouter";
import { GenericApiOkResp } from "@shared/genericApiTypes";

export default function PlayerModal() {
    const { isModalOpen, closeModal, playerRef } = usePlayerModalStateValue();
    const { hasPerm } = useAdminPerms();
    const setLocation = useLocation()[1];

    const [currRefreshKey, setCurrRefreshKey] = useState(0);
    const [modalData, setModalData] = useState<PlayerModalSuccess | undefined>(undefined);
    const [modalError, setModalError] = useState('');
    const [tsFetch, setTsFetch] = useState(0);
    
    // Action Panel State: 'dashboard' | 'message' | 'kick' | 'warn' | 'ban'
    const [activeAction, setActiveAction] = useState<'dashboard' | 'message' | 'kick' | 'warn' | 'ban'>('dashboard');
    const [actionReason, setActionReason] = useState('');
    const [isSubmittingAction, setIsSubmittingAction] = useState(false);

    // References for action forms
    const banFormRef = useRef<BanFormType>(null);
    const notesTextAreaRef = useRef<HTMLTextAreaElement>(null);
    const [notesLogText, setNotesLogText] = useState('');

    const playerQueryApi = useBackendApi<PlayerModalResp>({
        method: 'GET',
        path: `/player`,
        abortOnUnmount: true,
    });

    const playerWhitelistApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/whitelist`,
    });

    const playerNotesApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/save_note`,
    });

    const playerMessageApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/message`,
    });

    const playerKickApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/kick`,
    });

    const playerWarnApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/warn`,
    });

    const playerBanApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/ban`,
        throwGenericErrors: true,
    });

    const refreshModalData = () => {
        setCurrRefreshKey(prev => prev + 1);
    };

    // Query player detailed data
    useEffect(() => {
        if (!playerRef) return;
        setModalData(undefined);
        setModalError('');
        playerQueryApi({
            queryParams: playerRef,
            success: (resp) => {
                if ('error' in resp) {
                    setModalError(resp.error);
                } else {
                    setModalData(resp);
                    setTsFetch(Math.round(Date.now() / 1000));
                    if (notesTextAreaRef.current) {
                        notesTextAreaRef.current.value = resp.player.notes ?? '';
                    }
                    if (!('license' in playerRef) && resp.player.license) {
                        setPlayerModalUrlParam(resp.player.license);
                    }
                }
            },
            error: (error) => {
                setModalError(error);
            },
        });
    }, [playerRef, currRefreshKey]);

    // Reset view state when modal closes/opens
    useEffect(() => {
        if (!isModalOpen) {
            setTimeout(() => {
                setActiveAction('dashboard');
                setActionReason('');
                setIsSubmittingAction(false);
                setNotesLogText('');
            }, 200);
        }
    }, [isModalOpen]);

    const handleOpenClose = (newOpenState: boolean) => {
        if (isModalOpen && !newOpenState) {
            closeModal();
        }
    };

    // Whitelist toggling
    const handleWhitelistToggle = () => {
        if (!playerRef || !modalData) return;
        playerWhitelistApi({
            queryParams: playerRef,
            data: {
                status: !modalData.player.tsWhitelisted
            },
            toastLoadingMessage: 'Updating whitelist...',
            genericHandler: {
                successMsg: 'Whitelist status updated.',
            },
            success: (data) => {
                if ('success' in data) {
                    refreshModalData();
                }
            },
        });
    };

    // Give Admin Redirect
    const handleGiveAdmin = () => {
        if (!modalData?.player) return;
        const p = modalData.player;
        const params = new URLSearchParams();
        params.set("autofill", "true");
        params.set("name", p.pureName);
        for (const id of p.ids) {
            if (id.startsWith("discord:")) {
                params.set("discord", id);
            } else if (id.startsWith("fivem:")) {
                params.set("citizenfx", id);
            }
        }
        setLocation(`/admins?${params.toString()}`);
        closeModal();
    };

    // Notes auto-save
    const savePlayerNotes = () => {
        if (!playerRef || !modalData?.player) return;
        setNotesLogText('Saving...');
        playerNotesApi({
            queryParams: playerRef,
            data: {
                note: notesTextAreaRef.current?.value.trim(),
            },
            success: (data) => {
                if ('error' in data) {
                    setNotesLogText(data.error);
                } else {
                    setNotesLogText('Saved successfully.');
                    refreshModalData();
                }
            },
            error: (err) => {
                setNotesLogText(`Error: ${err}`);
            }
        });
    };

    // Action execution handler (DM, Kick, Warn, Ban)
    const handleActionSubmit = () => {
        if (!playerRef || !modalData?.player) return;

        if (activeAction !== 'ban' && !actionReason.trim() && activeAction !== 'kick') {
            txToast.warning('A reason or message is required.');
            return;
        }

        setIsSubmittingAction(true);

        if (activeAction === 'message') {
            playerMessageApi({
                queryParams: playerRef,
                data: { message: actionReason.trim() },
                toastLoadingMessage: 'Sending direct message...',
                genericHandler: { successMsg: 'Direct message sent.' },
                success: () => {
                    setIsSubmittingAction(false);
                    setActiveAction('dashboard');
                    setActionReason('');
                    refreshModalData();
                },
                error: () => setIsSubmittingAction(false),
            });
        } else if (activeAction === 'kick') {
            playerKickApi({
                queryParams: playerRef,
                data: { reason: actionReason.trim() },
                toastLoadingMessage: 'Kicking player...',
                genericHandler: { successMsg: 'Player kicked.' },
                success: () => {
                    setIsSubmittingAction(false);
                    setActiveAction('dashboard');
                    setActionReason('');
                    refreshModalData();
                },
                error: () => setIsSubmittingAction(false),
            });
        } else if (activeAction === 'warn') {
            playerWarnApi({
                queryParams: playerRef,
                data: { reason: actionReason.trim() },
                toastLoadingMessage: 'Sending warning...',
                genericHandler: { successMsg: 'Warning issued.' },
                success: () => {
                    setIsSubmittingAction(false);
                    setActiveAction('dashboard');
                    setActionReason('');
                    refreshModalData();
                },
                error: () => setIsSubmittingAction(false),
            });
        } else if (activeAction === 'ban') {
            if (!banFormRef.current) return;
            const { reason, duration } = banFormRef.current.getData();

            if (!reason || reason.length < 3) {
                txToast.warning(`The reason must be at least 3 characters long.`);
                banFormRef.current.focusReason();
                setIsSubmittingAction(false);
                return;
            }

            playerBanApi({
                queryParams: playerRef,
                data: { reason, duration },
                toastLoadingMessage: 'Banning player...',
                genericHandler: { successMsg: 'Player banned.' },
                success: () => {
                    setIsSubmittingAction(false);
                    setActiveAction('dashboard');
                    refreshModalData();
                },
                error: () => setIsSubmittingAction(false),
            });
        }
    };

    // Derived statistics strings
    const sessionTimeText = useMemo(() => {
        if (!modalData?.player.sessionTime) return '--';
        return msToDuration(modalData.player.sessionTime * 60_000, { units: ['h', 'm'] });
    }, [modalData?.player.sessionTime]);

    const playTimeText = useMemo(() => {
        if (!modalData?.player.playTime) return '--';
        return msToDuration(modalData.player.playTime * 60_000, { units: ['d', 'h', 'm'] });
    }, [modalData?.player.playTime]);

    const banCount = useMemo(() => {
        if (!modalData?.player) return 0;
        return modalData.player.actionHistory.filter((a) => a.type === 'ban' && !a.revokedAt).length;
    }, [modalData?.player]);

    const warnCount = useMemo(() => {
        if (!modalData?.player) return 0;
        return modalData.player.actionHistory.filter((a) => a.type === 'warn' && !a.revokedAt).length;
    }, [modalData?.player]);

    const isBannedText = useMemo(() => {
        if (!modalData?.player || !modalData.serverTime) return null;
        let banExpiration;
        for (const action of modalData.player.actionHistory) {
            if (action.type !== 'ban' || action.revokedAt) continue;
            if (action.exp) {
                if (action.exp >= modalData.serverTime) {
                    banExpiration = Math.max(banExpiration ?? 0, action.exp);
                }
            } else {
                return 'Permanently Banned';
            }
        }
        if (banExpiration !== undefined) {
            const str = tsToLocaleDateTimeString(banExpiration, 'short', 'short');
            return `Banned until ${str}`;
        }
        return null;
    }, [modalData?.player, modalData?.serverTime]);

    // Role display badges
    const isOwner = modalData?.player.displayName === 'aisc0pez' || modalData?.player.license === 'abcd123456efgh';

    return (
        <Dialog open={isModalOpen} onOpenChange={handleOpenClose}>
            <DialogContent className="max-w-4xl p-0 gap-0 flex flex-col bg-[#070b13] border-[#1f2d4d] overflow-hidden text-slate-100 shadow-[0_10px_50px_rgba(0,0,0,0.8),0_0_30px_rgba(31,45,77,0.2)]">
                {/* Header */}
                <DialogHeader className="px-6 py-4 border-b border-[#1f2d4d] bg-[#0c1322]">
                    <DialogTitle className="flex items-center gap-3 text-lg font-bold tracking-wide">
                        {modalData?.player ? (
                            <>
                                {modalData.player.isConnected ? (
                                    <span className="flex h-2 w-2 rounded-full bg-[#00ff66]" />
                                ) : (
                                    <span className="flex h-2.5 w-2.5 rounded-full bg-slate-500" />
                                )}
                                <span className="text-slate-400 font-mono text-sm bg-slate-900/60 px-2 py-0.5 rounded border border-slate-800">
                                    {modalData.player.isConnected ? `ID: ${modalData.player.netid ?? '?'}` : 'OFF'}
                                </span>
                                <span className="text-xl text-slate-100 font-semibold tracking-wide">
                                    {modalData.player.displayName}
                                </span>
                            </>
                        ) : modalError ? (
                            <span className="text-destructive-inline flex items-center gap-1.5"><ShieldAlertIcon className="h-5" /> Error loading data</span>
                        ) : (
                            <span className="text-muted-foreground italic flex items-center gap-2"><Loader2Icon className="h-4 animate-spin" /> Retrieving profile...</span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                {/* Dashboard Two-Column Layout */}
                <div className="flex flex-col md:flex-row h-full min-h-[500px] max-h-[75vh]">
                    
                    {/* LEFT COLUMN: Profile Hero & Deck */}
                    <div className="w-full md:w-80 flex flex-col items-center p-6 border-r border-[#1f2d4d] bg-[#0a0e1a]/80">
                        {modalData?.player ? (
                            <>
                                {/* Large Avatar */}
                                <div className="relative group mb-4">
                                    <div className={cn(
                                        "absolute inset-0 rounded-full blur-md opacity-40 transition-all duration-300 group-hover:opacity-75",
                                        modalData.player.isConnected 
                                            ? "bg-gradient-to-r from-emerald-500 to-cyan-500 shadow-[0_0_20px_rgba(16,185,129,0.4)]" 
                                            : "bg-slate-500"
                                    )} />
                                    <img
                                        src={modalData.player.profilePicture ?? `https://api.dicebear.com/7.x/identicon/svg?seed=${modalData.player.pureName}`}
                                        alt={modalData.player.displayName}
                                        className={cn(
                                            "relative w-24 h-24 rounded-full border-2 object-cover shadow-inner",
                                            modalData.player.isConnected ? "border-emerald-500/70" : "border-slate-600"
                                        )}
                                    />
                                    {modalData.player.isConnected && (
                                        <span className="absolute bottom-1 right-1 h-4 w-4 rounded-full bg-emerald-500 border-2 border-[#070b13] shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                                    )}
                                </div>

                                {/* Username display */}
                                <h3 className="text-lg font-bold text-center text-slate-200 leading-tight">
                                    {modalData.player.pureName}
                                </h3>

                                {/* Role Badges */}
                                <div className="flex flex-wrap justify-center gap-1.5 mt-2 mb-6">
                                    {isOwner && (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white border border-purple-400/20 shadow-sm shadow-purple-500/20">
                                            Owner
                                        </span>
                                    )}
                                    {modalData.player.isAdmin && !isOwner && (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                                            Admin
                                        </span>
                                    )}
                                    {modalData.player.tsWhitelisted && (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                            Whitelisted
                                        </span>
                                    )}
                                    {isBannedText && (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30">
                                            Banned
                                        </span>
                                    )}
                                </div>

                                {/* Command Button Deck */}
                                <div className="w-full flex flex-col gap-2 mt-auto">
                                    <Button
                                        variant="ghost"
                                        className={cn(
                                            "w-full justify-start text-xs border border-transparent tracking-wide text-slate-300 font-semibold h-9 pl-3 rounded-lg transition-all duration-300",
                                            "hover:bg-[#121b2f] hover:text-indigo-400 hover:border-indigo-500/30"
                                        )}
                                        disabled={!hasPerm('manage.admins') || !modalData.player.ids.length}
                                        onClick={handleGiveAdmin}
                                    >
                                        <ShieldCheckIcon className="h-4 w-4 mr-2 text-indigo-400" />
                                        Give Admin
                                    </Button>

                                    <Button
                                        variant="ghost"
                                        className={cn(
                                            "w-full justify-start text-xs border border-transparent tracking-wide text-slate-300 font-semibold h-9 pl-3 rounded-lg transition-all duration-300",
                                            activeAction === 'message' ? "bg-indigo-600/20 text-indigo-400 border-indigo-500/50" : "hover:bg-[#121b2f] hover:text-indigo-400 hover:border-indigo-500/30"
                                        )}
                                        disabled={!hasPerm('players.direct_message') || !modalData.player.isConnected}
                                        onClick={() => setActiveAction(activeAction === 'message' ? 'dashboard' : 'message')}
                                    >
                                        <MailIcon className="h-4 w-4 mr-2 text-cyan-400" />
                                        Direct Message
                                    </Button>

                                    <Button
                                        variant="ghost"
                                        className={cn(
                                            "w-full justify-start text-xs border border-transparent tracking-wide text-slate-300 font-semibold h-9 pl-3 rounded-lg transition-all duration-300",
                                            activeAction === 'kick' ? "bg-yellow-600/20 text-yellow-400 border-yellow-500/50" : "hover:bg-[#121b2f] hover:text-yellow-400 hover:border-yellow-500/30"
                                        )}
                                        disabled={!hasPerm('players.kick') || !modalData.player.isConnected}
                                        onClick={() => setActiveAction(activeAction === 'kick' ? 'dashboard' : 'kick')}
                                    >
                                        <LogOut className="h-4 w-4 mr-2 text-yellow-400" />
                                        Kick Player
                                    </Button>

                                    <Button
                                        variant="ghost"
                                        className={cn(
                                            "w-full justify-start text-xs border border-transparent tracking-wide text-slate-300 font-semibold h-9 pl-3 rounded-lg transition-all duration-300",
                                            activeAction === 'warn' ? "bg-orange-600/20 text-orange-400 border-orange-500/50" : "hover:bg-[#121b2f] hover:text-orange-400 hover:border-orange-500/30"
                                        )}
                                        disabled={!hasPerm('players.warn')}
                                        onClick={() => setActiveAction(activeAction === 'warn' ? 'dashboard' : 'warn')}
                                    >
                                        <AlertTriangleIcon className="h-4 w-4 mr-2 text-orange-400" />
                                        Warn Player
                                    </Button>

                                    <Button
                                        variant="ghost"
                                        className={cn(
                                            "w-full justify-start text-xs border border-transparent tracking-wide text-slate-300 font-semibold h-9 pl-3 rounded-lg transition-all duration-300",
                                            activeAction === 'ban' ? "bg-red-600/20 text-red-400 border-red-500/50" : "hover:bg-[#121b2f] hover:text-red-400 hover:border-red-500/30"
                                        )}
                                        disabled={!hasPerm('players.ban')}
                                        onClick={() => setActiveAction(activeAction === 'ban' ? 'dashboard' : 'ban')}
                                    >
                                        <GavelIcon className="h-4 w-4 mr-2 text-red-500" />
                                        Ban Player
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="w-full flex items-center justify-center h-full">
                                <GenericSpinner msg="Preparing deck..." />
                            </div>
                        )}
                    </div>

                    {/* RIGHT COLUMN: Interactive Dashboard Views */}
                    <div className="flex-1 flex flex-col bg-[#080d19]/90 overflow-hidden">
                        
                        {!modalData ? (
                            <div className="flex-grow flex items-center justify-center p-8">
                                {modalError ? (
                                    <div className="text-center">
                                        <ShieldAlertIcon className="h-12 w-12 text-red-500 mx-auto mb-3" />
                                        <p className="text-red-400 font-semibold">{modalError}</p>
                                    </div>
                                ) : (
                                    <GenericSpinner msg="Loading stats..." />
                                )}
                            </div>
                        ) : (
                            <ScrollArea className="flex-1 p-6">
                                
                                {activeAction === 'dashboard' && (
                                    <div className="space-y-6">
                                        {/* Ban Warning Alert Card */}
                                        {isBannedText && (
                                            <div className="p-3 bg-red-950/30 border border-red-500/30 rounded-xl flex items-center gap-3">
                                                <ShieldAlertIcon className="size-6 text-red-500" />
                                                <div>
                                                    <h4 className="text-sm font-bold text-red-400">Sanction Warning</h4>
                                                    <p className="text-xs text-red-200/80">{isBannedText}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Stats Grid */}
                                        <div>
                                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Player Statistics</h4>
                                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                                {modalData.player.isConnected && (
                                                    <div className="bg-[#0c1322] border border-[#1f2d4d]/50 p-3.5 rounded-xl flex flex-col">
                                                        <span className="text-slate-400 font-medium text-xs mb-1 flex items-center gap-1"><ClockIcon className="h-3.5 text-indigo-400" /> Session</span>
                                                        <span className="text-slate-200 font-bold text-sm tracking-wide mt-auto">{sessionTimeText}</span>
                                                    </div>
                                                )}
                                                <div className="bg-[#0c1322] border border-[#1f2d4d]/50 p-3.5 rounded-xl flex flex-col">
                                                    <span className="text-slate-400 font-medium text-xs mb-1 flex items-center gap-1"><UserIcon className="h-3.5 text-cyan-400" /> Total Play</span>
                                                    <span className="text-slate-200 font-bold text-sm tracking-wide mt-auto">{playTimeText}</span>
                                                </div>
                                                <div className="bg-[#0c1322] border border-[#1f2d4d]/50 p-3.5 rounded-xl flex flex-col">
                                                    <span className="text-slate-400 font-medium text-xs mb-1 flex items-center gap-1"><CalendarIcon className="h-3.5 text-emerald-400" /> Join Date</span>
                                                    <span className="text-slate-200 font-bold text-xs tracking-wide mt-auto">
                                                        {modalData.player.tsJoined ? (
                                                            <DateTimeCorrected serverTime={modalData.serverTime} tsObject={modalData.player.tsJoined} tsFetch={tsFetch} isDateOnly />
                                                        ) : '--'}
                                                    </span>
                                                </div>
                                                {!modalData.player.isConnected && (
                                                    <div className="bg-[#0c1322] border border-[#1f2d4d]/50 p-3.5 rounded-xl flex flex-col">
                                                        <span className="text-slate-400 font-medium text-xs mb-1 flex items-center gap-1"><ClockIcon className="h-3.5 text-amber-500" /> Last Connected</span>
                                                        <span className="text-slate-200 font-bold text-xs tracking-wide mt-auto">
                                                            {modalData.player.tsLastConnection ? (
                                                                <DateTimeCorrected serverTime={modalData.serverTime} tsObject={modalData.player.tsLastConnection} tsFetch={tsFetch} isDateOnly />
                                                            ) : '--'}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="bg-[#0c1322] border border-[#1f2d4d]/50 p-3.5 rounded-xl flex flex-col col-span-2 lg:col-span-1">
                                                    <span className="text-slate-400 font-medium text-xs mb-1 flex items-center gap-1"><GavelIcon className="h-3.5 text-red-400" /> Violations</span>
                                                    <span className="text-slate-200 font-bold text-xs mt-auto flex gap-2">
                                                        <span className="bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded text-2xs font-semibold">{banCount} Bans</span>
                                                        <span className="bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded text-2xs font-semibold">{warnCount} Warns</span>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Whitelist, Give Admin / Quick settings */}
                                        <div className="flex items-center justify-between p-3.5 bg-[#0c1322] border border-[#1f2d4d]/50 rounded-xl">
                                            <div>
                                                <h5 className="text-sm font-bold text-slate-200">Whitelisting Status</h5>
                                                <p className="text-xs text-slate-400">
                                                    {modalData.player.tsWhitelisted ? (
                                                        <>Approved on <DateTimeCorrected serverTime={modalData.serverTime} tsObject={modalData.player.tsWhitelisted} tsFetch={tsFetch} isDateOnly /></>
                                                    ) : 'Currently not Whitelisted'}
                                                </p>
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="border-[#1f2d4d] hover:bg-[#121b2f] hover:text-[#00ffd1]"
                                                disabled={!hasPerm('players.whitelist')}
                                                onClick={handleWhitelistToggle}
                                            >
                                                {modalData.player.tsWhitelisted ? 'Revoke Whitelist' : 'Add to Whitelist'}
                                            </Button>
                                        </div>

                                        {/* Notes Card */}
                                        <div className="bg-[#0c1322] border border-[#1f2d4d]/50 rounded-xl p-4 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label htmlFor="playerNotes" className="text-xs font-bold uppercase tracking-wider text-slate-400">
                                                    Administrator Notes
                                                </Label>
                                                <span className="text-[10px] text-[#00ffd1] font-mono">{notesLogText}</span>
                                            </div>
                                            <Textarea
                                                ref={notesTextAreaRef}
                                                id="playerNotes"
                                                className="w-full mt-1 bg-[#070b13] border-[#1f2d4d] text-slate-100 placeholder-slate-500 rounded-lg text-sm"
                                                disabled={!modalData.player.isRegistered}
                                                defaultValue={modalData.player.notes}
                                                onChange={() => setNotesLogText('Press save button to submit')}
                                                placeholder={modalData.player.isRegistered
                                                    ? 'Type notes regarding warnings, bans, or player behaviour...'
                                                    : 'Cannot set notes for players that are not registered.'}
                                            />
                                            <div className="flex justify-end pt-1">
                                                <Button
                                                    variant="secondary"
                                                    size="xs"
                                                    onClick={savePlayerNotes}
                                                    disabled={!modalData.player.isRegistered}
                                                    className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs"
                                                >
                                                    Save Note
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Sanctions Log */}
                                        <div className="bg-[#0c1322] border border-[#1f2d4d]/50 rounded-xl p-4 flex flex-col max-h-[300px] overflow-hidden">
                                            <span className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 block">Sanctions Log</span>
                                            <ScrollArea className="flex-grow">
                                                <PlayerHistoryTab actionHistory={modalData.player.actionHistory} serverTime={modalData.serverTime} refreshModalData={refreshModalData} />
                                            </ScrollArea>
                                        </div>

                                        {/* Copyable IDs */}
                                        <div className="bg-[#0c1322] border border-[#1f2d4d]/50 rounded-xl p-4 flex flex-col">
                                            <PlayerIdsTab player={modalData.player} refreshModalData={refreshModalData} />
                                        </div>
                                    </div>
                                )}

                                {/* DM message inline form */}
                                {activeAction === 'message' && (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 pb-2 border-b border-[#1f2d4d]">
                                            <Button variant="ghost" size="xs" onClick={() => setActiveAction('dashboard')} className="text-slate-400 hover:text-slate-200">
                                                <ChevronLeftIcon className="h-4 w-4" /> Back
                                            </Button>
                                            <h4 className="text-sm font-bold text-slate-200 flex items-center gap-1.5">
                                                <MailIcon className="h-4 text-cyan-400" /> Direct Message to {modalData.player.displayName}
                                            </h4>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="messageInput" className="text-xs text-slate-400">Direct message text</Label>
                                            <Textarea
                                                id="messageInput"
                                                className="bg-[#070b13] border-[#1f2d4d] min-h-[120px] rounded-lg"
                                                placeholder="Type the message that will pop up on the player's screen..."
                                                value={actionReason}
                                                onChange={(e) => setActionReason(e.target.value)}
                                            />
                                        </div>
                                        <div className="flex justify-end gap-2 pt-2">
                                            <Button variant="ghost" onClick={() => { setActiveAction('dashboard'); setActionReason(''); }} disabled={isSubmittingAction}>
                                                Cancel
                                            </Button>
                                            <Button 
                                                onClick={handleActionSubmit} 
                                                disabled={isSubmittingAction || !actionReason.trim()}
                                                className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold"
                                            >
                                                {isSubmittingAction ? <><Loader2Icon className="animate-spin h-4 mr-2" /> Sending...</> : <><SendIcon className="h-4 mr-2" /> Send Message</>}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Kick inline form */}
                                {activeAction === 'kick' && (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 pb-2 border-b border-[#1f2d4d]">
                                            <Button variant="ghost" size="xs" onClick={() => setActiveAction('dashboard')} className="text-slate-400 hover:text-slate-200">
                                                <ChevronLeftIcon className="h-4 w-4" /> Back
                                            </Button>
                                            <h4 className="text-sm font-bold text-slate-200 flex items-center gap-1.5">
                                                <LogOut className="h-4 text-yellow-400" /> Kick {modalData.player.displayName}
                                            </h4>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="kickInput" className="text-xs text-slate-400">Reason for kick</Label>
                                            <Textarea
                                                id="kickInput"
                                                className="bg-[#070b13] border-[#1f2d4d] min-h-[100px] rounded-lg"
                                                placeholder="Enter kick reason (can be blank)..."
                                                value={actionReason}
                                                onChange={(e) => setActionReason(e.target.value)}
                                            />
                                        </div>
                                        <div className="flex justify-end gap-2 pt-2">
                                            <Button variant="ghost" onClick={() => { setActiveAction('dashboard'); setActionReason(''); }} disabled={isSubmittingAction}>
                                                Cancel
                                            </Button>
                                            <Button 
                                                onClick={handleActionSubmit} 
                                                disabled={isSubmittingAction}
                                                className="bg-yellow-600 hover:bg-yellow-500 text-black font-bold"
                                            >
                                                {isSubmittingAction ? <><Loader2Icon className="animate-spin h-4 mr-2" /> Kicking...</> : <><LogOut className="h-4 mr-2" /> Kick Player</>}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Warn inline form */}
                                {activeAction === 'warn' && (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 pb-2 border-b border-[#1f2d4d]">
                                            <Button variant="ghost" size="xs" onClick={() => setActiveAction('dashboard')} className="text-slate-400 hover:text-slate-200">
                                                <ChevronLeftIcon className="h-4 w-4" /> Back
                                            </Button>
                                            <h4 className="text-sm font-bold text-slate-200 flex items-center gap-1.5">
                                                <AlertTriangleIcon className="h-4 text-orange-400" /> Issue Warning to {modalData.player.displayName}
                                            </h4>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="warnInput" className="text-xs text-slate-400">Violation Reason</Label>
                                            <Textarea
                                                id="warnInput"
                                                className="bg-[#070b13] border-[#1f2d4d] min-h-[120px] rounded-lg"
                                                placeholder="Provide detailed explanation for rule warning (e.g. FailRP / VDM in Safezone)..."
                                                value={actionReason}
                                                onChange={(e) => setActionReason(e.target.value)}
                                            />
                                        </div>
                                        <div className="flex justify-end gap-2 pt-2">
                                            <Button variant="ghost" onClick={() => { setActiveAction('dashboard'); setActionReason(''); }} disabled={isSubmittingAction}>
                                                Cancel
                                            </Button>
                                            <Button 
                                                onClick={handleActionSubmit} 
                                                disabled={isSubmittingAction || !actionReason.trim()}
                                                className="bg-orange-600 hover:bg-orange-500 text-white font-bold"
                                            >
                                                {isSubmittingAction ? <><Loader2Icon className="animate-spin h-4 mr-2" /> Issuing...</> : <><AlertTriangleIcon className="h-4 mr-2" /> Warn Player</>}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Ban inline form */}
                                {activeAction === 'ban' && (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 pb-2 border-b border-[#1f2d4d]">
                                            <Button variant="ghost" size="xs" onClick={() => setActiveAction('dashboard')} className="text-slate-400 hover:text-slate-200">
                                                <ChevronLeftIcon className="h-4 w-4" /> Back
                                            </Button>
                                            <h4 className="text-sm font-bold text-slate-200 flex items-center gap-1.5">
                                                <GavelIcon className="h-4 text-red-500" /> Apply Server Ban to {modalData.player.displayName}
                                            </h4>
                                        </div>
                                        
                                        <BanForm
                                            ref={banFormRef}
                                            banTemplates={modalData.banTemplates}
                                            disabled={isSubmittingAction}
                                            onNavigateAway={() => { setActiveAction('dashboard'); }}
                                        />

                                        <div className="flex justify-end gap-2 pt-2">
                                            <Button variant="ghost" onClick={() => { setActiveAction('dashboard'); }} disabled={isSubmittingAction}>
                                                Cancel
                                            </Button>
                                            <Button 
                                                onClick={handleActionSubmit} 
                                                disabled={isSubmittingAction}
                                                className="bg-red-600 hover:bg-red-500 text-white font-bold"
                                            >
                                                {isSubmittingAction ? <><Loader2Icon className="animate-spin h-4 mr-2" /> Banning...</> : <><GavelIcon className="h-4 mr-2" /> Confirm Ban</>}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </ScrollArea>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
